import React from 'react';
import { styled } from '@mui/material';
import { Colors } from 'library';

interface TextInputProps {
    value: string;
    onChange: (value: string) => void;
    onBlur?: () => void;
    onSubmit?: () => void;
    id?: string;
    placeholder?: string;
    disabled?: boolean;
    fullWidth?: boolean;
    error?: string;
    color?: 'white' | 'default';
    startAdornment?: React.ReactNode;
    endAdornment?: React.ReactNode;
}

export function TextInput({
    value,
    onChange,
    onBlur,
    onSubmit,
    id,
    placeholder,
    disabled,
    fullWidth,
    error,
    color = 'default',
    startAdornment,
    endAdornment,
}: TextInputProps) {
    return (
        <Wrapper fullWidth={fullWidth}>
            <Field fullWidth={fullWidth} fieldColor={color} hasError={!!error} disabled={disabled}>
                {startAdornment && <Adornment>{startAdornment}</Adornment>}
                <BareInput
                    id={id}
                    type="text"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onBlur={onBlur}
                    onKeyDown={onSubmit ? (e) => e.key === 'Enter' && onSubmit() : undefined}
                    placeholder={placeholder}
                    disabled={disabled}
                />
                {endAdornment && <Adornment>{endAdornment}</Adornment>}
            </Field>
            {error && <ErrorMessage>{error}</ErrorMessage>}
        </Wrapper>
    );
}

const Wrapper = styled('div')<{ fullWidth?: boolean }>(({ fullWidth }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    width: fullWidth ? '100%' : 'auto',
}));

const ErrorMessage = styled('span')({
    fontSize: 12,
    color: Colors.error,
});

const Field = styled('div', {
    shouldForwardProp: (prop) => !['fullWidth', 'fieldColor', 'hasError', 'disabled'].includes(prop as string),
})<{ fullWidth?: boolean; fieldColor?: 'white' | 'default'; hasError?: boolean; disabled?: boolean }>(
    ({ fullWidth, fieldColor, hasError, disabled }) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: fullWidth ? '100%' : 'auto',
        padding: '8px 10px',
        backgroundColor: disabled ? Colors.fill : fieldColor === 'white' ? Colors.white : Colors.surface,
        border: `1px solid ${hasError ? Colors.error : Colors.border}`,
        borderRadius: 10,
        boxSizing: 'border-box',
        '&:focus-within': {
            borderColor: hasError ? Colors.error : Colors.textSecondary,
        },
    }),
);

const Adornment = styled('span')({
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
});

const BareInput = styled('input')({
    flex: 1,
    minWidth: 0,
    padding: 0,
    border: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
    fontSize: 14,
    color: Colors.primary,
    '&:disabled': {
        color: Colors.textSecondary,
    },
    '&::placeholder': {
        color: Colors.border,
    },
});
